import {
  compileTypedCalculations, createTypedExpressionSession, TYPED_FUNCTIONS, TYPED_OPERATORS,
  typedArgumentContract, TYPED_EXPRESSION_LIMITS, type TypedArgumentContract, type TypedFields, type TypedFunctionId, type TypedOperatorId,
  type TypedExpressionRoot, type TypedResultType, type TypedScalar, type TypedScope, type TypedValueType,
} from "../nativeExpression.mjs";
import { asArray, asRecord, cloneValue, type JsonRecord } from "./reportDesignerModel";
import { formulaColumn, formulaTable } from "./internalFormulaSession";
import { reportParameters } from "./parameterAuthoring";
import { resolveReportParameterValues } from "../nativeParameters.mjs";

type OperationId = TypedFunctionId | TypedOperatorId;
type Presentation = readonly [string, string, string, readonly string[]];
const presentation: Record<OperationId, Presentation> = {
  concat: ["Combine text", "Text", "Join two or more text values, in order.", ["Text"]],
  trim: ["Remove surrounding spaces", "Text", "Remove spaces around text. Null stays null.", ["Text"]],
  length: ["Count characters", "Text", "Count Unicode characters in text.", ["Text"]],
  abs: ["Absolute value", "Numbers", "Return the positive magnitude of a number.", ["Number"]],
  round: ["Round a number", "Numbers", "Round to -6 through 6 decimal places, ties away from zero.", ["Number", "Decimal places"]],
  floor: ["Round down", "Numbers", "Round toward negative infinity.", ["Number"]],
  ceiling: ["Round up", "Numbers", "Round toward positive infinity.", ["Number"]],
  min: ["Smallest number", "Numbers", "Choose the smallest number. Null propagates.", ["Number"]],
  max: ["Largest number", "Numbers", "Choose the largest number. Null propagates.", ["Number"]],
  add: ["Add numbers", "Numbers", "Add two or more numeric values.", ["Number"]],
  sub: ["Subtract numbers", "Numbers", "Negate one value or subtract the second from the first.", ["Number"]],
  mul: ["Multiply numbers", "Numbers", "Multiply two or more numeric values.", ["Number"]],
  div: ["Divide numbers", "Numbers", "Divide the first number by a nonzero second number.", ["Numerator", "Denominator"]],
  and: ["All conditions are true", "Conditions", "Test that every Boolean condition is true.", ["Condition"]],
  or: ["Any condition is true", "Conditions", "Test that at least one Boolean condition is true.", ["Condition"]],
  not: ["Reverse a condition", "Conditions", "Return the opposite Boolean value.", ["Condition"]],
  eq: ["Values are equal", "Conditions", "Compare matching scalar types, including null.", ["First value", "Second value"]],
  neq: ["Values are different", "Conditions", "Compare matching scalar types, including null.", ["First value", "Second value"]],
  gt: ["Greater than", "Conditions", "Compare non-null numbers or dates of the same type.", ["First value", "Second value"]],
  gte: ["Greater than or equal", "Conditions", "Compare non-null numbers or dates of the same type.", ["First value", "Second value"]],
  lt: ["Less than", "Conditions", "Compare non-null numbers or dates of the same type.", ["First value", "Second value"]],
  lte: ["Less than or equal", "Conditions", "Compare non-null numbers or dates of the same type.", ["First value", "Second value"]],
  if: ["If a condition is true", "Conditional & null", "Choose one of two values with matching types. Only the chosen branch is evaluated.", ["Condition", "Value when true", "Value when false"]],
  is_null: ["Value is missing", "Conditional & null", "Check whether a value is explicitly null.", ["Value"]],
  coalesce: ["Use the first available value", "Conditional & null", "Choose the first non-null value. Zero, false and empty text are retained.", ["Value"]],
  nullif: ["Leave blank when equal", "Conditional & null", "Return null when two values match; otherwise return the first.", ["First value", "Second value"]],
  year: ["Year from a date", "Dates", "Extract the year without timezone conversion.", ["Date"]],
  month: ["Month from a date", "Dates", "Extract the month, 1 through 12.", ["Date"]],
  day: ["Day from a date", "Dates", "Extract the day of the month.", ["Date"]],
  date_add: ["Add days to a date", "Dates", "Shift a date by an integer number of calendar days.", ["Date", "Days to add", "Unit"]],
  date_diff: ["Days between two dates", "Dates", "Return the second date minus the first in calendar days.", ["First date", "Second date", "Unit"]],
};
export type FormulaOperation = {
  id: OperationId; kind: "call" | "op"; title: string; category: string; description: string;
  labels: readonly string[]; minArgs: number; maxArgs: number;
};
export const FORMULA_OPERATIONS: readonly FormulaOperation[] = [
  ...Object.entries(TYPED_FUNCTIONS).map(([id, signature]) => operation(id as TypedFunctionId, "call", signature)),
  ...Object.entries(TYPED_OPERATORS).map(([id, signature]) => operation(id as TypedOperatorId, "op", signature)),
];
function operation(id: OperationId, kind: "call" | "op", signature: { minArgs: number; maxArgs: number }): FormulaOperation {
  const [title, category, description, labels] = presentation[id];
  return { id, kind, title, category, description, labels, ...signature };
}
export function formulaOperation(node: unknown) {
  const value = asRecord(node);
  return FORMULA_OPERATIONS.find(item => item.kind === ("op" in value ? "op" : "call") && item.id === (value.op || value.call));
}
export function argumentLabel(operation: FormulaOperation, index: number): string {
  return operation.labels[index] || `${operation.labels[operation.labels.length - 1]} ${index + 1}`;
}
export function formulaSignature(operation: FormulaOperation): string {
  const parameters = Array.from({ length: operation.minArgs }, (_, index) => {
    const contract = typedArgumentContract(operation.kind, operation.id, index)!;
    return `${argumentLabel(operation, index)}: ${contract.choices ? contract.choices.map(value => `"${value}"`).join(" | ") : contract.types.join(" | ")}${contract.nullable ? "?" : ""}`;
  });
  return `${operation.id.toUpperCase()}(${parameters.join(", ")}${operation.maxArgs > operation.minArgs ? ", ..." : ""})`;
}
export function argumentContract(node: unknown, index: number): TypedArgumentContract | null {
  const operation = formulaOperation(node);
  return operation ? typedArgumentContract(operation.kind, operation.id, index) : null;
}
export function seedOperation(operation: FormulaOperation): JsonRecord {
  return { [operation.kind]: operation.id, args: Array.from({ length: operation.minArgs }, (_, index) => {
    const choices = typedArgumentContract(operation.kind, operation.id, index)?.choices;
    return choices ? { const: choices[0] } : undefined;
  }) };
}
export function nodeAt(root: unknown, path: readonly number[]): unknown {
  return path.reduce<unknown>((node, index) => asArray(asRecord(node).args)[index], root);
}
export function replaceNode(root: unknown, path: readonly number[], next: unknown): unknown {
  if (!path.length) return next;
  const value = asRecord(root);
  return { ...value, args: asArray(value.args).map((child, index) => index === path[0] ? replaceNode(child, path.slice(1), next) : child) };
}
export function formulaSummary(node: unknown): string {
  const value = asRecord(node);
  const operation = formulaOperation(node);
  if (operation) return operation.title;
  if ("const" in value) return value.const === null ? `Null (${value.valueType || "type required"})` : `${String(value.const).slice(0, 70) || 'Empty text'}`;
  if ("field" in value) return `${String(value.scope)}: ${String(value.field)}`;
  if ("calc" in value) return `Calculated: ${String(value.calc)}`;
  if ("param" in value) return `Parameter: ${String(value.param)}`;
  return node === undefined ? "Choose a value" : `Unsupported expression${value.call ? `: ${String(value.call)}` : ""}`;
}
export type FormulaField = { key: string; label: string; path: string; scope: TypedScope; type: TypedValueType; nullable: boolean };
export function formulaFields(source: unknown): FormulaField[] {
  const fields: FormulaField[] = [];
  for (const dataset of asArray(asRecord(asRecord(source).data).datasets).map(asRecord)) {
    if (dataset.id !== formulaTable(source).dataset && dataset.kind !== "single") continue;
    for (const field of asArray(dataset.fields).map(asRecord)) {
      if (typeof field.path !== "string" || !["string", "number", "date", "boolean"].includes(String(field.type)) || typeof field.nullable !== "boolean") continue;
      const scope = dataset.kind === "single" ? "document" : "detail";
      const path = scope === "document" ? `${String(dataset.id)}.${field.path}` : field.path;
      fields.push({ key: `${scope}:${path}`, path, scope, label: String(field.label || field.path), type: field.type as TypedValueType, nullable: field.nullable });
    }
  }
  return fields;
}
export function formulaCompileOptions(source: unknown) {
  const fields: TypedFields = { detail: {}, document: {} };
  for (const field of formulaFields(source)) fields[field.scope]![field.path] = { type: field.type, nullable: field.nullable };
  return { fields, allowedScopes: ["detail", "document"] as TypedScope[], parameters: reportParameters(source) };
}
function declarations(source: unknown, targetId: string, node: unknown, remove: boolean) {
  const values: Record<string, unknown> = Object.create(null);
  for (const column of asArray(formulaTable(source).columns).map(asRecord)) {
    if (column.id === targetId) { if (!remove) values[targetId] = { typed: node }; }
    else if (column.expr !== undefined || column.calculation !== undefined) values[String(column.id)] = column.expr ?? column.calculation;
  }
  return values;
}
export type FormulaAnalysis = {
  valid: boolean; error?: string; type?: TypedResultType; canonical?: TypedExpressionRoot;
  value?: TypedScalar; previewError?: string; noSample?: boolean; validationMs: number; evaluationMs: number;
};
function missingDraftValue(node: unknown, label = "the formula", depth = 0): string | undefined {
  if (node === undefined) return label;
  const operation = formulaOperation(node);
  if (!operation || depth >= TYPED_EXPRESSION_LIMITS.depth) return undefined;
  const args = asArray(asRecord(node).args).slice(0, TYPED_EXPRESSION_LIMITS.arguments);
  for (let index = 0; index < args.length; index++) {
    const missing = missingDraftValue(args[index], `${operation.title} / ${argumentLabel(operation, index)}`, depth + 1);
    if (missing) return missing;
  }
  return undefined;
}
export function analyzeFormula(source: unknown, targetId: string, node: unknown, sampleIndex = 0, remove = false, preview = true, inputs?: Readonly<Record<string, unknown>>, filter = false): FormulaAnalysis {
  const start = performance.now();
  try {
    if (!filter && !Object.keys(formulaColumn(source, targetId)).length) throw new Error("The formula target is no longer available.");
    const options = formulaCompileOptions(source);
    const plan = compileTypedCalculations(declarations(source, filter ? "" : targetId, node, remove), options);
    const filterPlan = filter && !remove ? plan.compile({ typed: node }) : undefined;
    if (filterPlan && (filterPlan.type.type !== "boolean" || filterPlan.type.nullable)) throw new Error("Presentation filters must return nonnullable boolean.");
    const validationMs = performance.now() - start;
    const result: FormulaAnalysis = { valid: true, validationMs, evaluationMs: 0, type: filterPlan?.type || plan.types[targetId], canonical: filterPlan?.canonical || plan.canonical[targetId] as TypedExpressionRoot | undefined };
    if (remove || !preview) return result;
    const datasets = asRecord(asRecord(source).datasets);
    const row = asArray(datasets[String(formulaTable(source).dataset)]).slice(0, 25)[sampleIndex];
    if (!row) return { ...result, noSample: true };
    const document: JsonRecord = {};
    for (const dataset of asArray(asRecord(asRecord(source).data).datasets).map(asRecord)) {
      if (dataset.kind === "single") document[String(dataset.id)] = datasets[String(dataset.id)];
    }
    const evaluationStart = performance.now();
    try {
      const session = createTypedExpressionSession({ limits: { work: 100000 } });
      const context = { detail: asRecord(row), document, parameters: resolveReportParameterValues(options.parameters, inputs) };
      const values = plan.evaluate(context, session);
      result.value = filterPlan ? session.evaluate(filterPlan, context, values) : values[targetId];
    } catch (error) { result.previewError = error instanceof Error ? error.message : "Sample evaluation failed."; }
    result.evaluationMs = performance.now() - evaluationStart;
    return result;
  } catch (error) {
    const missing = missingDraftValue(node);
    return { valid: false, error: missing ? `Choose a value for ${missing}.` : error instanceof Error ? error.message : "Invalid formula.", validationMs: performance.now() - start, evaluationMs: 0 };
  }
}
export function eligibleCalculations(source: unknown, targetId: string, filter = false): { id: string; type: TypedResultType; disabled: boolean; reason?: string }[] {
  const plan = compileTypedCalculations(declarations(source, "", undefined, true), formulaCompileOptions(source));
  return Object.entries(plan.types).map(([id, type]) => {
    const probe = filter ? { valid: true, error: undefined } : analyzeFormula(source, targetId, { calc: id }, 0, false, false);
    return { id, type, disabled: !probe.valid, reason: probe.error };
  });
}
export function committedFormula(source: unknown, targetId: string): unknown {
  const root = asRecord(formulaColumn(source, targetId).expr);
  return root.typed === undefined ? undefined : cloneValue(root.typed);
}
